/**
 * Super Admin User Management Types
 * 
 * Defines types, interfaces, and helper functions for the super admin user management system
 * with hierarchical roles and multi-municipality access control.
 * 
 * @module superAdmin
 * @version 4.0
 * @date 2026-01-24
 */

// ========================================
// SUPER ADMIN ROLES
// ========================================

/**
 * Super Admin Role Hierarchy
 * 
 * - SUPER_ADMIN: Full system control, can perform any action
 * - ADMINISTRATOR: Full control, creates other admins with notification
 * - REGION_MANAGER: Can invite Content Managers and Support to assigned municipalities
 * - CONTENT_MANAGER: Manages content in assigned municipalities
 * - ACCOUNTANT: Financial operations in assigned municipalities
 * - SUPPORT: User support and moderation in assigned municipalities
 */
export enum SuperAdminRole {
  SUPER_ADMIN = 'super_admin',
  ADMINISTRATOR = 'administrator',
  REGION_MANAGER = 'region_manager',
  CONTENT_MANAGER = 'content_manager',
  ACCOUNTANT = 'accountant',
  SUPPORT = 'support',
}

// ========================================
// ACCESS LEVELS
// ========================================

/**
 * Municipality Access Levels
 * 
 * Defines the level of access a super admin user has to a municipality
 */
export enum MunicipalityAccessLevel {
  NONE = 'none',
  READ_ONLY = 'read_only',
  CONTENT_MANAGER = 'content_manager',
  FULL_ACCESS = 'full_access',
}

// ========================================
// PERMISSIONS
// ========================================

/**
 * Granular Municipality Permissions
 *
 * 18 boolean flags controlling specific permissions within a municipality
 */
export interface MunicipalityPermissions {
  // Content Management
  canEditNews: boolean;
  canManageEvents: boolean;
  canManageSurveys: boolean;
  canManagePOIs: boolean;
  canManageIssues: boolean;
  canManageLostAndFound: boolean;
  canManageVolunteering: boolean;

  // Governance
  canManageCouncil: boolean;
  canManageMayor: boolean;
  canManageCommittees: boolean;

  // Operations
  canManageTasks: boolean;
  canAccessChat: boolean;
  canManageSocialMedia: boolean;
  canManageOnlinePayments: boolean;
  /**
   * Grants access to the push notifications module.
   * Only municipality_admin role members may send emergencyAlert campaigns
   * regardless of this permission — enforced server-side in the Cloud Function.
   */
  canManagePushNotifications: boolean;

  // Analytics & Settings
  canViewAnalytics: boolean;
  canManageUsers: boolean;
  canEditMunicipalitySettings: boolean;
}

// ========================================
// ASSIGNMENTS
// ========================================

/**
 * Municipality Assignment
 * 
 * Represents a super admin user's assignment to a specific municipality
 * with granular permissions and optional expiration
 */
export interface MunicipalityAssignment {
  // Core assignment data
  municipalityId: string;
  accessLevel: MunicipalityAccessLevel;
  permissions: MunicipalityPermissions;
  
  // Metadata
  grantedBy: string;  // User ID of who granted access
  grantedAt: Date;
  expiresAt?: Date | null;  // Optional expiration
  notes?: string;  // Optional admin notes
  
  // Analytics
  lastAccessed?: Date;  // For tracking usage
}

// ========================================
// SUPER ADMIN USER
// ========================================

/**
 * Super Admin User Interface
 * 
 * Extends base User with super admin specific fields
 */
export interface SuperAdminUser {
  id: string;
  email: string;
  displayName?: string;
  name?: string;
  surname?: string;
  photoURL?: string;
  phoneNumber?: string;  // With country code
  
  // Role information
  role: 'super_admin';  // Base role
  superAdminRole: SuperAdminRole;  // Specific super admin role
  
  // Municipality access
  municipalityAssignments: MunicipalityAssignment[];
  assignedMunicipalityIds?: string[];  // Denormalized flat array for Firestore security rules
  selectedMunicipalityId?: string;  // Runtime state for multi-municipality users
  
  // Status
  status: 'active' | 'inactive' | 'pending';
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  lastLogin?: Date;
  
  // Invitation tracking
  invitedBy?: string;
  invitedAt?: Date;
}

// ========================================
// EXTENDED USER (for AuthContext)
// ========================================

/**
 * Extended User Type
 * 
 * Union type that supports both municipality users and super admin users
 * Used in AuthContext to handle all user types
 */
export interface ExtendedUser {
  id: string;
  email: string;
  displayName?: string;
  name?: string;
  surname?: string;
  photoURL?: string;
  role: string;
  
  // Municipality user fields
  municipalityId?: string;
  municipalityRole?: string;
  
  // Super admin fields
  superAdminRole?: SuperAdminRole;
  municipalityAssignments?: MunicipalityAssignment[];
  assignedMunicipalityIds?: string[];
  selectedMunicipalityId?: string;
  
  // Common fields
  status?: 'active' | 'inactive' | 'pending';
  createdAt?: Date;
  updatedAt?: Date;
  lastLogin?: Date;
}

// ========================================
// DENORMALIZED INDEX
// ========================================

/**
 * Municipality Member Index
 * 
 * Denormalized index for efficient querying of all users with access to a municipality
 * Stored at: /municipality_members/{municipalityId}/members/{userId}
 */
export interface MunicipalityMemberIndex {
  // User identification
  userId: string;
  userName: string;
  userEmail: string;
  
  // Access details
  role: 'municipality_admin' | 'municipality_user' | 'super_admin';
  superAdminRole?: SuperAdminRole;
  accessLevel?: MunicipalityAccessLevel;
  
  // Metadata
  grantedAt: Date;
  grantedBy?: string;
  expiresAt?: Date;
  
  // Status
  isActive: boolean;  // Quick check: !expired && accessLevel !== 'none'
  lastAccessed?: Date;
  
  // Tracking
  updatedAt: Date;
}

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Convert Firestore Timestamp or Date-like value to JavaScript Date.
 * Firestore returns Timestamp objects that have toDate(); plain Date works as-is.
 */
const toDateSafe = (value: unknown): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const fn = (value as { toDate: () => Date }).toDate;
    if (typeof fn === 'function') return fn.call(value);
  }
  try {
    const d = new Date(value as string | number);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

/**
 * Check if assignment is currently valid
 * 
 * Validates that:
 * - Access level is not 'none'
 * - Assignment has not expired
 * 
 * Handles Firestore Timestamp for expiresAt (from Firestore reads).
 * 
 * @param assignment - Municipality assignment to validate
 * @returns true if assignment is valid, false otherwise
 */
export const isAssignmentValid = (
  assignment: MunicipalityAssignment & { expiresAt?: unknown }
): boolean => {
  if (!assignment) return false;
  
  // Check access level
  if (assignment.accessLevel === MunicipalityAccessLevel.NONE) {
    return false;
  }
  
  // Check expiration - support Firestore Timestamp (has toDate) and plain Date
  const expiresAt = assignment.expiresAt;
  if (expiresAt != null) {
    const expirationDate = toDateSafe(expiresAt);
    if (expirationDate) {
      const now = new Date();
      if (expirationDate < now) {
        return false;
      }
    }
  }
  
  return true;
};

/**
 * Check if assignment has expired (expiresAt in the past).
 * Handles Firestore Timestamp for expiresAt.
 */
export const isAssignmentExpired = (
  assignment: MunicipalityAssignment & { expiresAt?: unknown }
): boolean => {
  if (!assignment?.expiresAt) return false;
  const expirationDate = toDateSafe(assignment.expiresAt);
  return expirationDate != null && expirationDate < new Date();
};

/**
 * Check if user has access to a municipality
 * 
 * Handles three cases:
 * 1. Municipality admin/user with single municipalityId
 * 2. Super admin with selected municipality (multi-municipality users)
 * 3. Super admin with any assignment to municipality
 * 
 * @param user - Extended user object
 * @param municipalityId - Municipality ID to check access for
 * @returns true if user has access, false otherwise
 */
export const hasAccessToMunicipality = (
  user: ExtendedUser | null | undefined,
  municipalityId: string
): boolean => {
  if (!user || !municipalityId) return false;
  
  // Case 1: Municipality admin/user with single municipalityId
  if (user.municipalityId === municipalityId) {
    return true;
  }
  
  // Case 2: Super admin with selected municipality
  if (user.selectedMunicipalityId === municipalityId && user.municipalityAssignments) {
    const assignment = user.municipalityAssignments.find(
      (a) => a.municipalityId === municipalityId
    );
    
    if (!assignment) return false;
    
    return isAssignmentValid(assignment);
  }
  
  // Case 3: Check if user has any assignment to this municipality
  if (user.municipalityAssignments) {
    const assignment = user.municipalityAssignments.find(
      (a) => a.municipalityId === municipalityId
    );

    if (!assignment) return false;

    return isAssignmentValid(assignment);
  }

  // Case 4: Fallback - check denormalized assignedMunicipalityIds (region managers)
  if (user.assignedMunicipalityIds && user.assignedMunicipalityIds.includes(municipalityId)) {
    return true;
  }

  return false;
};

/**
 * Check if inviter can invite target role
 * 
 * Invitation hierarchy:
 * - Super Admin / Administrator: Can invite all roles
 * - Region Manager: Can invite Content Manager and Support only
 * - Others: Cannot invite
 * 
 * @param inviterRole - Super admin role of the inviter
 * @param targetRole - Super admin role to invite
 * @returns true if invitation is allowed, false otherwise
 */
export const canInviteRole = (
  inviterRole: SuperAdminRole,
  targetRole: SuperAdminRole
): boolean => {
  // Super Admin and Administrator can invite all roles
  if (
    inviterRole === SuperAdminRole.SUPER_ADMIN ||
    inviterRole === SuperAdminRole.ADMINISTRATOR
  ) {
    return true;
  }
  
  // Region Manager can only invite Content Manager and Support
  if (inviterRole === SuperAdminRole.REGION_MANAGER) {
    return (
      targetRole === SuperAdminRole.CONTENT_MANAGER ||
      targetRole === SuperAdminRole.SUPPORT
    );
  }
  
  // All other roles cannot invite
  return false;
};

/**
 * Check if user can assign someone to a municipality
 * 
 * Assignment rules:
 * - Super Admin / Administrator: Can assign to any municipality
 * - Region Manager: Can only assign to municipalities they're assigned to
 * - Others: Cannot assign
 * 
 * @param inviter - Extended user object of the inviter
 * @param municipalityId - Municipality to assign to
 * @returns true if assignment is allowed, false otherwise
 */
export const canAssignToMunicipality = (
  inviter: ExtendedUser | null | undefined,
  municipalityId: string
): boolean => {
  if (!inviter || !municipalityId) return false;
  
  // Super Admin and Administrator can assign to any municipality
  if (
    inviter.superAdminRole === SuperAdminRole.SUPER_ADMIN ||
    inviter.superAdminRole === SuperAdminRole.ADMINISTRATOR
  ) {
    return true;
  }
  
  // Region Manager can only assign to municipalities they're assigned to
  if (inviter.superAdminRole === SuperAdminRole.REGION_MANAGER) {
    return hasAccessToMunicipality(inviter, municipalityId);
  }
  
  // All other roles cannot assign
  return false;
};

/**
 * Get list of roles that user can invite
 * 
 * @param inviterRole - Super admin role of the inviter
 * @returns Array of super admin roles that can be invited
 */
export const getInvitableRoles = (
  inviterRole: SuperAdminRole
): SuperAdminRole[] => {
  // Super Admin and Administrator can invite all roles
  if (
    inviterRole === SuperAdminRole.SUPER_ADMIN ||
    inviterRole === SuperAdminRole.ADMINISTRATOR
  ) {
    return [
      SuperAdminRole.SUPER_ADMIN,
      SuperAdminRole.ADMINISTRATOR,
      SuperAdminRole.REGION_MANAGER,
      SuperAdminRole.CONTENT_MANAGER,
      SuperAdminRole.ACCOUNTANT,
      SuperAdminRole.SUPPORT,
    ];
  }
  
  // Region Manager can invite Content Manager and Support
  if (inviterRole === SuperAdminRole.REGION_MANAGER) {
    return [
      SuperAdminRole.CONTENT_MANAGER,
      SuperAdminRole.SUPPORT,
    ];
  }
  
  // All other roles cannot invite
  return [];
};

/**
 * Get permission preset based on access level
 * 
 * Provides default permission configurations for each access level
 * 
 * @param accessLevel - Municipality access level
 * @returns Permissions object with defaults for the access level
 */
export const getPermissionPreset = (
  accessLevel: MunicipalityAccessLevel
): MunicipalityPermissions => {
  const basePermissions: MunicipalityPermissions = {
    canEditNews: false,
    canManageEvents: false,
    canManageSurveys: false,
    canManagePOIs: false,
    canManageIssues: false,
    canManageLostAndFound: false,
    canManageVolunteering: false,
    canManageCouncil: false,
    canManageMayor: false,
    canManageCommittees: false,
    canManageTasks: false,
    canAccessChat: false,
    canManageSocialMedia: false,
    canManageOnlinePayments: false,
    canManagePushNotifications: false,
    canViewAnalytics: false,
    canManageUsers: false,
    canEditMunicipalitySettings: false,
  };
  
  switch (accessLevel) {
    case MunicipalityAccessLevel.NONE:
      return basePermissions;
      
    case MunicipalityAccessLevel.READ_ONLY:
      return {
        ...basePermissions,
        canViewAnalytics: true,
      };
      
    case MunicipalityAccessLevel.CONTENT_MANAGER:
      return {
        ...basePermissions,
        canEditNews: true,
        canManageEvents: true,
        canManageSurveys: true,
        canManagePOIs: true,
        canManageIssues: true,
        canManageLostAndFound: true,
        canManageVolunteering: true,
        canManageCouncil: true,
        canManageMayor: true,
        canManageCommittees: true,
        canManageTasks: true,
        // NO chat access by default for content managers
        canAccessChat: false,
        canManageSocialMedia: true,
        canManageOnlinePayments: false,
        // Push notifications require explicit grant — not included by default for content managers
        canManagePushNotifications: false,
        canViewAnalytics: true,
        canManageUsers: false,
        canEditMunicipalitySettings: false,
      };

    case MunicipalityAccessLevel.FULL_ACCESS:
      return {
        canEditNews: true,
        canManageEvents: true,
        canManageSurveys: true,
        canManagePOIs: true,
        canManageIssues: true,
        canManageLostAndFound: true,
        canManageVolunteering: true,
        canManageCouncil: true,
        canManageMayor: true,
        canManageCommittees: true,
        canManageTasks: true,
        canAccessChat: true,
        canManageSocialMedia: true,
        canManageOnlinePayments: true,
        canManagePushNotifications: true,
        canViewAnalytics: true,
        canManageUsers: true,
        canEditMunicipalitySettings: true,
      };
      
    default:
      return basePermissions;
  }
};

/**
 * Validate permissions for potential conflicts
 * 
 * Checks for logical conflicts in permission combinations
 * Returns warnings but doesn't prevent saving (all combinations technically valid)
 * 
 * @param permissions - Permissions object to validate
 * @returns Validation result with warnings array
 */
export const validatePermissions = (
  permissions: MunicipalityPermissions
): { isValid: boolean; warnings: string[] } => {
  const warnings: string[] = [];
  
  // Conflict: Can manage surveys but can't view analytics
  if (permissions.canManageSurveys && !permissions.canViewAnalytics) {
    warnings.push('Users who manage surveys should be able to view analytics');
  }
  
  // Conflict: Can manage council but can't manage mayor
  if (permissions.canManageCouncil && !permissions.canManageMayor) {
    warnings.push('Managing council typically includes managing mayor');
  }
  
  // Conflict: Can edit municipality settings but can't manage other content
  if (
    permissions.canEditMunicipalitySettings &&
    !permissions.canEditNews &&
    !permissions.canManageEvents
  ) {
    warnings.push('Users with settings access typically need content management permissions');
  }
  
  // All permissions are technically valid, warnings are just suggestions
  return {
    isValid: true,
    warnings,
  };
};

/**
 * Check if user has specific permission for current municipality
 * 
 * @param user - Extended user object
 * @param permission - Permission key to check
 * @returns true if user has the permission, false otherwise
 */
export const hasPermission = (
  user: ExtendedUser | null | undefined,
  permission: keyof MunicipalityPermissions
): boolean => {
  if (!user) return false;
  
  // Municipality admin/user: full access
  if (user.municipalityId) {
    return true;
  }
  
  // Super admin with selected municipality: check permissions
  if (user.selectedMunicipalityId && user.municipalityAssignments) {
    const assignment = user.municipalityAssignments.find(
      (a) => a.municipalityId === user.selectedMunicipalityId
    );
    
    if (!assignment || !isAssignmentValid(assignment)) {
      return false;
    }
    
    // Check specific permission
    return assignment.permissions?.[permission] === true;
  }
  
  return false;
};

/**
 * Get human-readable label for super admin role
 * 
 * @param role - Super admin role
 * @returns Human-readable role label
 */
export const getSuperAdminRoleLabel = (role: SuperAdminRole): string => {
  switch (role) {
    case SuperAdminRole.SUPER_ADMIN:
      return 'Super admin';
    case SuperAdminRole.ADMINISTRATOR:
      return 'Administrator';
    case SuperAdminRole.REGION_MANAGER:
      return 'Region manager';
    case SuperAdminRole.CONTENT_MANAGER:
      return 'Content manager';
    case SuperAdminRole.ACCOUNTANT:
      return 'Accountant';
    case SuperAdminRole.SUPPORT:
      return 'Support';
    default:
      return 'Unknown';
  }
};

/**
 * Get human-readable label for access level
 * 
 * @param accessLevel - Municipality access level
 * @returns Human-readable access level label
 */
export const getAccessLevelLabel = (accessLevel: MunicipalityAccessLevel): string => {
  switch (accessLevel) {
    case MunicipalityAccessLevel.NONE:
      return 'No access';
    case MunicipalityAccessLevel.READ_ONLY:
      return 'Read only';
    case MunicipalityAccessLevel.CONTENT_MANAGER:
      return 'Content manager';
    case MunicipalityAccessLevel.FULL_ACCESS:
      return 'Full access';
    default:
      return 'Unknown';
  }
};
